import TableLayout from "@/components/layouts/page/TableLayout";

import {useEffect, useMemo, useState} from "react";

import {
  getUsersRanking,
  setPage,
  setSort,
  setUsersRankingDate,
} from "@redux/slices/dashboardSlices/dashboardCallSlice";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {Head} from "../../components/Head";
import styles from "./styles.module.scss";

export const UserRanking = () => {
  const minDate = moment().subtract(10, "years").toDate();
  const [withArchived, setWithArchived] = useState(false);

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const {
    data,
    sort,
    status,
    error,
    page,
    size,
    date: usersRankingDate,
  } = useSelector((state) => state.dashboard.call.usersRanking);

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
    dispatch(setPage(1));
  };

  const columns = [
    {
      Header: t("table.rank"),
      accessor: "rank",
      width: 100,
    },
    {Header: t("table.name"), accessor: "user", width: 245},
    {
      Header: t("table.role"),
      accessor: "role",
      width: 260,
    },
    {
      Header: t("table.product_line"),
      accessor: "product_line",
      width: 280,
    },
    {
      Header: t("table.average_score"),
      accessor: "avg_score",
      Cell: ({value}) => {
        const num = Number(value);
        return (
          <span style={{textAlign: "center"}}>
            {!isNaN(num) ? num.toFixed(1) : ""}
          </span>
        );
      },
      width: 120,
    },
    {
      Header: t("table.completed"),
      accessor: "count",
      Cell: ({value}) => {
        return <span style={{textAlign: "center"}}>{value}</span>;
      },
      width: 150,
    },
    {
      Header: "",
      accessor: "emptyEnd",
      width: 0,
    },
  ];

  useEffect(() => {
    dispatch(setPage(1));
  }, [usersRankingDate.from, usersRankingDate.to, dispatch]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * size;
    const end = start + size;

    return data.slice(start, end);
  }, [data, page, size]);

  const totalPages = useMemo(() => {
    return Math.ceil((data?.length || 0) / size) || 1;
  }, [data, size]);

  const handlePageChange = (selectedPage) => {
    dispatch(setPage(selectedPage + 1));
  };

  const updateUsersRanking = async () => {
    await dispatch(
      getUsersRanking({
        fromDate: usersRankingDate.from,
        toDate: usersRankingDate.to,
        withArchived,
        sort,
      }),
    );
  };

  const onSetDate = async (key, date) => {
    const isoValue = date instanceof Date ? date.toISOString() : date;
    const newDate = {...usersRankingDate, [key]: isoValue};

    await dispatch(setUsersRankingDate(newDate));
    await dispatch(
      getUsersRanking({
        fromDate: newDate.from,
        toDate: newDate.to,
        withArchived,
        sort,
      }),
    );
  };

  useEffect(() => {
    updateUsersRanking();
  }, [withArchived, sort]);

  return (
    <div className={styles.rate}>
      <Head
        title={<span>{t("page.dashboard.user_rank")}</span>}
        checkboxLabel={t("page.dashboard.checkbox_filter")}
        setWithArchived={setWithArchived}
        withArchived={withArchived}
        chartsDate={usersRankingDate}
        minDate={minDate}
        setFromDate={(date) => onSetDate("from", date)}
        setToDate={(date) => onSetDate("to", date)}
        maxWidth={"100%"}
      />

      <TableLayout
        pagination
        settings={false}
        searchPlaceholder={t("table.search_users")}
        serverStatus={status}
        serverError={error}
        dataSort={sort}
        selectSort={handleSelectSort}
        columns={columns}
        data={paginatedData}
        dataSize={size}
        totalPages={totalPages}
        currentPage={page}
        toPage={handlePageChange}
        minHeight={440}
      />
    </div>
  );
};
